import React from 'react';
import type { FormikHelpers } from 'formik';
import { Formik } from 'formik';
import { isEmpty } from 'lodash';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import Button from '~/components/core/Atomic/Buttons/Button';
import Typography from '~/components/core/Atomic/Typography';
import CancelButton from '~/components/core/Buttons/CancelButton';
import type { PhoneProfileInfo, VoiceMessageConfigurationParams } from '~/server_shared/openapi/data-contracts';

import CardDialog from '../../../../CardDialog';
import type { UploadedFileData } from '../VoiceMessageConfigurationDialog/types';
import VoiceMessageConfigurationDialog from '../VoiceMessageConfigurationDialog/VoiceMessageConfigurationDialog';
import VoiceMessageDetailsPreview from '../VoiceMessageDetailsPreview/VoiceMessageDetailsPreview';

const FORM_IDS = {
  VOICEMAIL_TEXT_OR_RECORDED_MESSAGE: 'voicemail_text_or_recorded_message',
  VOICEMAIL_GREETING_TEXT: 'voicemail_greeting_text',
  VOICEMAIL_VOICE_FILE: 'voicemail_voice_file',
  OUT_OF_OFFICE_TEXT_OR_RECORDED_MESSAGE: 'out_of_office_text_or_recorded_message',
  OUT_OF_OFFICE_GREETING_TEXT: 'out_of_office_greeting_text',
  OUT_OF_OFFICE_VOICE_FILE: 'out_of_office_voice_file',
} as const;

interface FormValues {
  voicemail_text_or_recorded_message: 'text_message' | 'uploaded_text_message';
  voicemail_greeting_text: string;
  voicemail_voice_file?: UploadedFileData;
  out_of_office_text_or_recorded_message: 'text_message' | 'uploaded_text_message';
  out_of_office_greeting_text: string;
  out_of_office_voice_file?: UploadedFileData;
}

interface EditVoicemailConfigDialogProps {
  configuration: PhoneProfileInfo;
  onCancel: () => void;
  onSubmit: (params: {
    voicemail_message_configuration: VoiceMessageConfigurationParams;
    out_of_office_message_configuration: VoiceMessageConfigurationParams;
  }) => Promise<void>;
}

const EditVoicemailConfigDialog: React.FC<EditVoicemailConfigDialogProps> = ({ configuration, onCancel, onSubmit }) => {
  const { classes } = useStyles();
  const [allowSavingVoicemailWithoutNewFile, setAllowSavingVoicemailWithoutNewFile] = React.useState(
    !!configuration?.voicemail_message_configuration?.permanent_audio_file_url
  );

  const [allowSavingOutOfOfficeWithoutNewFile, setAllowSavingOutOfOfficeWithoutNewFile] = React.useState(
    !!configuration?.out_of_office_message_configuration?.permanent_audio_file_url
  );

  const handleSubmit = async (values: FormValues, formikProps: FormikHelpers<FormValues>) => {
    try {
      await onSubmit({
        voicemail_message_configuration: {
          configuration_type: values.voicemail_text_or_recorded_message,
          message_text: values.voicemail_greeting_text,
          stored_file_id: values.voicemail_voice_file?.stored_file_id,
        },
        out_of_office_message_configuration: {
          configuration_type: values.out_of_office_text_or_recorded_message,
          message_text: values.out_of_office_greeting_text,
          stored_file_id: values.out_of_office_voice_file?.stored_file_id,
        },
      });
      onCancel();
    } catch {
      formikProps.setSubmitting(false);
    }
  };

  return (
    <Formik<FormValues>
      initialValues={
        isEmpty(configuration)
          ? {
              voicemail_text_or_recorded_message: 'text_message',
              voicemail_greeting_text: '',
              voicemail_voice_file: undefined,
              out_of_office_text_or_recorded_message: 'text_message',
              out_of_office_greeting_text: '',
              out_of_office_voice_file: undefined,
            }
          : {
              voicemail_text_or_recorded_message:
                (configuration?.voicemail_message_configuration?.configuration_type as
                  | 'text_message'
                  | 'uploaded_text_message') || 'text_message',
              voicemail_greeting_text: configuration?.voicemail_message_configuration?.message_text || '',
              voicemail_voice_file: undefined,
              out_of_office_text_or_recorded_message:
                (configuration?.out_of_office_message_configuration?.configuration_type as
                  | 'text_message'
                  | 'uploaded_text_message') || 'text_message',
              out_of_office_greeting_text: configuration?.out_of_office_message_configuration?.message_text || '',
              out_of_office_voice_file: undefined,
            }
      }
      validationSchema={Yup.object().shape({
        voicemail_text_or_recorded_message: Yup.string().oneOf(['text_message', 'uploaded_text_message']).required(),
        voicemail_greeting_text: Yup.string().when('voicemail_text_or_recorded_message', {
          is: 'text_message',
          then: Yup.string().required('Please enter a message'),
        }),
        voicemail_voice_file: Yup.mixed().when('voicemail_text_or_recorded_message', {
          is: 'uploaded_text_message',
          then: allowSavingVoicemailWithoutNewFile ? Yup.mixed() : Yup.mixed().required('Please select a file'),
        }),
        out_of_office_text_or_recorded_message: Yup.string()
          .oneOf(['text_message', 'uploaded_text_message'])
          .required(),
        out_of_office_greeting_text: Yup.string().when('out_of_office_text_or_recorded_message', {
          is: 'text_message',
          then: Yup.string().required('Please enter a message'),
        }),
        out_of_office_voice_file: Yup.mixed().when('out_of_office_text_or_recorded_message', {
          is: 'uploaded_text_message',
          then: allowSavingOutOfOfficeWithoutNewFile ? Yup.mixed() : Yup.mixed().required('Please select a file'),
        }),
      })}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, handleSubmit: formikHandleSubmit }) => (
        <CardDialog
          title="Voicemail Configuration"
          isDialog
          maxWidth="sm"
          fullWidth
          onClose={onCancel}
          preventClose={isSubmitting}
        >
          <VoiceMessageConfigurationDialog
            title="Default voicemail message"
            voiceMessageConfiguration={configuration?.voicemail_message_configuration}
            textAreaLabel="Voicemail greeting"
            textAreaId={FORM_IDS.VOICEMAIL_GREETING_TEXT}
            voiceFileInputId={FORM_IDS.VOICEMAIL_VOICE_FILE}
            radioButtonGroupId={FORM_IDS.VOICEMAIL_TEXT_OR_RECORDED_MESSAGE}
            allowedTokens={{
              adjuster_name: {
                desc: 'Adjuster Name',
              },
            }}
            onReplaceExistingRecording={() => {
              setAllowSavingVoicemailWithoutNewFile(false);
            }}
          />
          <div className="mt-12" />
          <VoiceMessageConfigurationDialog
            title="Out of office voicemail message"
            voiceMessageConfiguration={configuration?.out_of_office_message_configuration}
            textAreaLabel="Out of office greeting"
            textAreaId={FORM_IDS.OUT_OF_OFFICE_GREETING_TEXT}
            voiceFileInputId={FORM_IDS.OUT_OF_OFFICE_VOICE_FILE}
            radioButtonGroupId={FORM_IDS.OUT_OF_OFFICE_TEXT_OR_RECORDED_MESSAGE}
            allowedTokens={{
              adjuster_name: {
                desc: 'Adjuster Name',
              },
              date_of_return: {
                desc: 'Date of Return',
              },
            }}
            onReplaceExistingRecording={() => {
              setAllowSavingOutOfOfficeWithoutNewFile(false);
            }}
          />

          <div className={classes.buttonsContainer}>
            <CancelButton disabled={isSubmitting} onClick={onCancel} />
            <Button
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              onClick={(e) => {
                e.preventDefault();
                formikHandleSubmit();
              }}
            >
              Save
            </Button>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

interface VoicemailConfigViewProps {
  profile: PhoneProfileInfo;
}

const VoicemailConfigView: React.FC<VoicemailConfigViewProps> = ({ profile }) => {
  return (
    <>
      <Typography variant="subtitle1">Configure voicemail settings</Typography>
      <Typography variant="caption">
        Upload a voice message or add a text message for the voicemail greeting played when you are busy on another call
        or out of office.
      </Typography>
      {profile.voicemail_message_enabled && !isEmpty(profile.voicemail_message_configuration) ? (
        <div className="mt-20">
          <VoiceMessageDetailsPreview
            configurationType={profile.voicemail_message_configuration?.configuration_type || ''}
            messageTextTitle="Voicemail by text message"
            messageText={profile.voicemail_message_configuration?.message_text ?? undefined}
            uploadedTextMessageTitle="Voicemail by recording"
            audioUrl={profile.voicemail_message_configuration?.permanent_audio_file_url ?? undefined}
          />
          <div className="mt-12" />
          <VoiceMessageDetailsPreview
            configurationType={profile.out_of_office_message_configuration?.configuration_type || ''}
            messageTextTitle="Out of office by text message"
            messageText={profile.out_of_office_message_configuration?.message_text ?? undefined}
            uploadedTextMessageTitle="Out of office  by recording"
            audioUrl={profile.out_of_office_message_configuration?.permanent_audio_file_url ?? undefined}
          />
        </div>
      ) : null}
    </>
  );
};

export { EditVoicemailConfigDialog, VoicemailConfigView };
